import { useSelector, useDispatch } from "react-redux";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { updateProfile, fetchUser } from "@/store/slices/authSlice";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import { FileText } from "lucide-react";

const Profile = () => {
  const { user } = useSelector((state) => state.auth);
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case "admin":
        return "default";
      case "district_admin":
        return "secondary";
      case "district_visitor":
        return "outline";
      case "applicant":
        return "outline";
      default:
        return "outline";
    }
  };

  // Change password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    setPasswordErrors({});

    const errs = {};
    if (!currentPassword)
      errs.currentPassword = "Please enter your current password.";
    if (!newPassword) errs.newPassword = "Please enter a new password.";
    if (!confirmPassword)
      errs.confirmPassword = "Please confirm the new password.";
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      errs.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(errs).length > 0) {
      setPasswordErrors(errs);
      return;
    }

    setIsChangingPassword(true);
    try {
      if (!user?.id) {
        toast({
          title: "Error",
          description: "User id not available. Please login again.",
          variant: "destructive",
        });
        setIsChangingPassword(false);
        return;
      }

      const payload = {
        user_id: user.id,
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      };

      const res = await api.post("/profile/change-password", payload);
      if (res && (res.status === 200 || res.status === 201)) {
        toast({
          title: "Success",
          description: "Password changed successfully.",
          variant: "success",
        });
        setNewPassword("");
        setConfirmPassword("");
        setPasswordErrors({});
      } else {
        toast({
          title: "Error",
          description: "Unable to change password.",
          variant: "destructive",
        });
      }
    } catch (err) {
      // map validation errors if present
      const respErrs =
        err?.response?.data?.errors || err?.response?.data || null;
      if (respErrs && typeof respErrs === "object") {
        const mapped = {};
        // Laravel style errors: { password: ['...'] }
        Object.entries(respErrs).forEach(([k, v]) => {
          const msg = Array.isArray(v) ? v[0] : String(v);
          if (
            k === "current_password" ||
            k === "password" ||
            k === "password_confirmation"
          ) {
            if (k === "current_password") mapped.currentPassword = msg;
            else if (k === "password") mapped.newPassword = msg;
            else if (k === "password_confirmation")
              mapped.confirmPassword = msg;
          } else if (k === "message") {
            toast({ title: "Error", description: msg });
          } else {
            // fallback, attach to confirm field
            mapped.confirmPassword = msg;
          }
        });
        setPasswordErrors(mapped);
      } else {
        toast({
          title: "Error",
          description: "Unable to change password. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // useEffect(() => {
  //   if (user) {
  //     console.log("Phone Verified At:", user?.phone_verified_at);
  //   }
  // }, [user]);


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Profile</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold">Name</label>
              <p className="text-sm">{user?.name}</p>
            </div>
            <div>
              <label className="text-sm font-bold">Email</label>
              <p className="text-sm">{user?.email}</p>
            </div>
            <div>
              <label className="text-sm font-bold">Phone</label>
              <p className="text-sm">{user?.phone}</p>
            </div>
            <div>
              <label className="text-sm font-bold">Role</label>
              <p className="text-sm">
                <Badge variant={getRoleBadgeVariant(user?.role)}>
                  {user?.role}
                </Badge>
              </p>
            </div>
          </div>

          {user?.district && (
            <div>
              <label className="text-sm font-medium">District</label>
              <p className="text-sm">{user.district.name}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Important Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Required Documents</h4>
              <ul className="text-sm space-y-1">
                <li>• Aadhaar Card Copy</li>
                <li>• PAN Card / GST Registration</li>
                <li>• Bank Account Details</li>
                <li>• EB Service Connection Bill</li>
                <li>• Machinery Invoice/Purchase Proof</li>
                <li>• Passport Size Photo</li>
                <li>• Digital Signature</li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Application Process</h4>
              <ul className="text-sm space-y-1">
                <li>1. Select the appropriate form</li>
                <li>2. Fill all required information</li>
                <li>3. Upload supporting documents</li>
                <li>4. Review and submit application</li>
                <li>5. Receive confirmation receipt</li>
                <li>6. Track application status</li>
              </ul>
            </div>
          </div>

          <div className="pt-4 border-t">
            <p className="text-xs">
              <strong>Note:</strong> All applications are subject to
              verification by concerned Circle Deputy Director/Assistant
              Directors. Ensure all information is accurate and supporting
              documents are clear and readable.
            </p>
          </div>
        </CardContent>
      </Card>

      {user.role == "applicant" && (
        <Card>
          <CardHeader>
            <CardTitle>Verification Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Phone Verification
                </label>
                <p className="text-sm">
                  <Badge
                    variant={
                      user?.is_verified === 1 ? "default" : "destructive"
                    }
                  >
                    {user?.is_verified === 1 ? "Verified" : "Pending"}
                  </Badge>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {user.role === "district_admin" && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Assigned Districts</CardTitle>
          </CardHeader>
          <CardContent className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {user?.assigned_districts?.length > 0 ? (
              user.assigned_districts.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-center h-[60px] rounded-lg bg-blue-50 text-blue-900 font-medium text-center shadow-sm hover:shadow-md transition-all"
                >
                  {item.name} ({item.code})
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No districts assigned.</p>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 mt-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className={
                  passwordErrors.currentPassword ? "border-red-500" : ""
                }
              />
              {passwordErrors.currentPassword && (
                <p className="text-sm text-red-500">
                  {passwordErrors.currentPassword}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className={passwordErrors.newPassword ? "border-red-500" : ""}
              />
              {passwordErrors.newPassword && (
                <p className="text-sm text-red-500">
                  {passwordErrors.newPassword}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                className={
                  passwordErrors.confirmPassword ? "border-red-500" : ""
                }
              />
              {passwordErrors.confirmPassword && (
                <p className="text-sm text-red-500">
                  {passwordErrors.confirmPassword}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleChangePassword}
              disabled={isChangingPassword}
            >
              {isChangingPassword ? "Changing..." : "Change Password"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
